import React, { useState, useEffect, useCallback } from "react";
import NavigationSidebar from "../components/Navigationsidebar/NavigationSidebar";
import Header from "../components/Header";
import D05StackedBarChart01 from "../components/Dashboardviz/D05/D05Viz01";
import BarChartViz02 from "../components/Dashboardviz/D05/D05Viz02";
import BarChartViz03 from "../components/Dashboardviz/D05/D05Viz03";
import BarChartViz04 from "../components/Dashboardviz/D05/D05Viz04";
import AverageScoresTable from "../components/Dashboardviz/D05/D05Viz05";
import WorkContextScoresTable from "../components/Dashboardviz/D05/D05Viz06";
import JobSatisfactionScoresTable from "../components/Dashboardviz/D05/D05Viz07";
import OrganizationalCommitmentScoresTable from "../components/Dashboardviz/D05/D05Viz08";
import FilterSidebar from "../components/FilterSidebar/FilterSidebar";
import ResponseDistributionTable from "../components/Dashboardviz/D05/D05Viz09";
import WorkContextDistributionTable from "../components/Dashboardviz/D05/D05Viz10";
import JobSatisfactionDistributionTable from "../components/Dashboardviz/D05/D05Viz11";
import OrganizationalCommitmentDistributionTable from "../components/Dashboardviz/D05/D05Viz12";
import { useFilter } from "../FilterContext";
import { FILTER_CONFIG } from "../config/filterconfig";
import jsonData from "../current-survey-numeric.json";
import "./D05_Response_Distribution.css";
import html2canvas from "html2canvas"; // Import html2canvas
import { useRef } from "react"; // Import useRef

const D05_Response_DistributionDashboard = () => {
  const { filterCriteria, setFilterCriteria } = useFilter();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isFilterSidebarOpen, setIsFilterSidebarOpen] = useState(false);
  const [filterData, setFilterData] = useState({});
  const [isNarrativeOpen, setIsNarrativeOpen] = useState(false);
  const [narrative, setNarrative] = useState("");
  // Refs for capturing screenshots
  const roleClarityRef = useRef(null);
  const workContextRef = useRef(null);
  const organizationalCommitmentRef = useRef(null);
  const jobSatisfactionRef = useRef(null);

  // Function to capture screenshot of a specific section
  const captureScreenshot = async (ref, fileName) => {
    if (ref.current) {
      const canvas = await html2canvas(ref.current, { scale: 2 });
      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = `${fileName}.png`;
      link.click();
    }
  };

  // Function to capture all screenshots in sequence
  const captureAllScreenshots = async () => {
    await captureScreenshot(roleClarityRef, "role-clarity");
    await captureScreenshot(workContextRef, "work-context");
    await captureScreenshot(jobSatisfactionRef, "job-satisfaction");
    await captureScreenshot(
      organizationalCommitmentRef,
      "organizational-commitment"
    );
  };
  useEffect(() => {
    const generateFilterOptions = () => {
      const options = {};
      FILTER_CONFIG.forEach(({ fieldName, jsonKey, sortOrder }) => {
        let uniqueValues = [...new Set(jsonData.map((item) => item[jsonKey]))];
        uniqueValues = uniqueValues.filter((value) => value !== null);

        if (sortOrder) {
          uniqueValues = uniqueValues.sort(
            (a, b) => sortOrder.indexOf(a) - sortOrder.indexOf(b)
          );
        } else {
          uniqueValues = uniqueValues.sort((a, b) =>
            a.localeCompare(b, undefined, { sensitivity: "base" })
          );
        }

        options[fieldName] = uniqueValues;
      });

      return options;
    };

    setFilterData(generateFilterOptions());
  }, []);

  const toggleSidebar = useCallback(() => {
    setIsSidebarOpen((prevState) => !prevState);
  }, []);

  const toggleFilterSidebar = useCallback(() => {
    setIsFilterSidebarOpen((prevState) => !prevState);
  }, []);

  // Update refresh page function to use window.location.reload()
  const refreshPage = useCallback(() => {
    window.location.reload();
  }, []);

  const toggleNarrative = () => {
    setIsNarrativeOpen((prevState) => !prevState);
  };

  const calculateAverage = (data, key) => {
    const validScores = data
      .map((item) => item[key])
      .filter((score) => typeof score === "number" && !isNaN(score));
    if (validScores.length === 0) {
      return "N/A"; // Return "N/A" when no valid scores are found
    }
    const total = validScores.reduce((sum, score) => sum + score, 0);
    return (total / validScores.length).toFixed(2);
  };

  const generateNarrative = (filteredData) => {
    if (filteredData.length === 0) {
      return "No data available for the selected filters.";
    }

    const engagementAreas = {
      "Role Clarity": ["MQ0044", "MQ0047", "MQ0017"],
      "Resources": ["MQ0048", "MQ0051", "MQ0050"],
      "Work Outcomes": ["MQ0095", "MQ0016", "MQ0059"],
      "Feedback": ["MQ0104", "MQ0173", "MQ0054"],
      "Safety": ["MQ0190", "MQ0141", "MQ0343"],
      "Compensation": ["MQ0088", "MQ0090", "MQ0091"],
      "Leadership": ["MQ0086", "MQ0034", "MQ0041"],
      "Communication": ["MQ0194", "MQ0321", "MQ0058"],
      "Supervision": ["MQ0056", "MQ0057", "MQ0038"],
      "Recognition": ["MQ0053", "MQ0171", "MQ0103"],
      "Autonomy": ["MQ0071", "MQ0202", "MQ0027"],
      "Meaningfulness": ["MQ0023", "MQ0024", "MQ0026"],
      "Growth": ["MQ0064", "MQ0062", "MQ0043"],
      "Teamwork": ["MQ0340", "MQ0341", "MQ0094"],
      "Belonging": ["MQ0021", "MQ0080", "MQ0302"],
      "Vision and Values": ["MQ0072", "MQ0029", "MQ0012"],
      "Inclusion": ["MQ0311", "MQ0312", "MQ0331"],
      "Openness": ["MQ0320", "MQ0046", "MQ0100"],
    };

    const processResponses = (data, questions) => {
      const responseCounts = {
        Unfavorable: 0,
        Neutral: 0,
        Favorable: 0,
        Total: 0,
      };

      data.forEach((item) => {
        questions.forEach((question) => {
          const response = item[question];
          if (response === 1 || response === 2) {
            responseCounts.Unfavorable++;
          } else if (response === 3) {
            responseCounts.Neutral++;
          } else if (response === 4 || response === 5) {
            responseCounts.Favorable++;
          }
          if (response !== null && response !== undefined) {
            responseCounts.Total++;
          }
        });
      });

      return {
        Favorable: responseCounts.Total
          ? ((responseCounts.Favorable / responseCounts.Total) * 100).toFixed(1)
          : "N/A",
        Unfavorable: responseCounts.Total
          ? ((responseCounts.Unfavorable / responseCounts.Total) * 100).toFixed(
              1
            )
          : "N/A",
        Neutral: responseCounts.Total
          ? ((responseCounts.Neutral / responseCounts.Total) * 100).toFixed(1)
          : "N/A",
      };
    };

    let strengths = [];
    let concerns = [];
    let detailedResults = [];

    Object.entries(engagementAreas).forEach(([areaName, questions]) => {
      const responseCounts = processResponses(filteredData, questions);

      if (responseCounts.Favorable !== "N/A") {
        detailedResults.push({
          area: areaName,
          favorability: parseFloat(responseCounts.Favorable),
        });
      }

      if (
        responseCounts.Favorable !== "N/A" &&
        responseCounts.Favorable >= 75
      ) {
        strengths.push(
          `${areaName} stands out with a favorability score of ${responseCounts.Favorable}%, showcasing excellent performance.`
        );
      }

      if (
        responseCounts.Unfavorable !== "N/A" &&
        responseCounts.Unfavorable >= 25
      ) {
        concerns.push(
          `${areaName} has ${responseCounts.Unfavorable}% unfavorable responses, indicating potential challenges.`
        );
      }
    });

    // Sort areas by favorability
    detailedResults.sort((a, b) => b.favorability - a.favorability);
    const top5 = detailedResults.slice(0, 5);
    const bottom5 = detailedResults.slice(-5);

    let narrative = "Summary of Engagement Insights:\n\n";

    if (strengths.length > 0) {
      narrative += "Key Strengths:\n" + strengths.join("\n") + "\n\n";
    } else {
      narrative += "Key Strengths:\nNo significant strengths identified.\n\n";
    }

    if (concerns.length > 0) {
      narrative += "Areas for Improvement:\n" + concerns.join("\n") + "\n\n";
    } else {
      narrative += "Areas for Improvement:\nNo major concerns identified.\n\n";
    }

    narrative += "Top 5 Engagement Areas:\n";
    narrative +=
      top5
        .map(
          (item, index) => `${index + 1}. ${item.area} (${item.favorability}%)`
        )
        .join("\n") + "\n\n";

    narrative += "Bottom 5 Engagement Areas:\n";
    narrative += bottom5
      .map(
        (item, index) => `${index + 1}. ${item.area} (${item.favorability}%)`
      )
      .join("\n");

    return narrative;
  };

  useEffect(() => {
    const filteredData = jsonData.filter((item) => {
      return Object.entries(filterCriteria).every(([key, value]) => {
        return value.length ? value.includes(item[key]) : true;
      });
    });

    console.log("Filtered Data:", filteredData); // Debugging log
    setNarrative(generateNarrative(filteredData));
  }, [filterCriteria]);

  const handleFilterChange = (updatedCriteria) => {
    setFilterCriteria(updatedCriteria);
  };

  return (
    <div className="D05_Response_Distribution-dashboard-container">
      <Header
        title="Response Distribution"
        toggleSidebar={toggleSidebar}
        refreshPage={refreshPage}
        toggleFilterSidebar={toggleFilterSidebar}
      />
      <NavigationSidebar
        className={isSidebarOpen ? "sidebar-open" : "sidebar-closed"}
        isOpen={isSidebarOpen}
      />
      <FilterSidebar
        className={
          isFilterSidebarOpen ? "filter-sidebar-open" : "filter-sidebar-closed"
        }
        isOpen={isFilterSidebarOpen}
        filterCriteria={filterCriteria}
        filterOptions={filterData}
        handleFilterChange={handleFilterChange}
      />

      {/* Button to trigger narrative modal */}
      <div className="D05_Response_Distribution-narrative-button-container">
        <button
          className="D05_Response_Distribution-narrative-button"
          onClick={toggleNarrative}
        >
          Narrative Insights
        </button>
        <button
          onClick={captureAllScreenshots}
          className="D05_Response_Distribution-screenshot-button"
        >
          Capture Screenshot
        </button>
      </div>

      <div className="D05_Response_Distribution-main-content">
        <div className="D05_Response_Distribution-charts-container">
          {/* First Row - Average Scores & Role Clarity */}
          <div
            ref={roleClarityRef}
            className="D05_Response_Distribution-capture-area"
          >
            <div className="D05_Response_Distribution-row three-columns">
              <div className="D05_Response_Distribution-chart-card">
                <h2 className="chart-title">Average Scores</h2>
                <AverageScoresTable filterCriteria={filterCriteria} />
              </div>
              <div className="D05_Response_Distribution-chart-card">
                <h2 className="chart-title">Role Clarity</h2>
                <D05StackedBarChart01 filterCriteria={filterCriteria} />
              </div>
              <div className="D05_Response_Distribution-chart-card">
                <h2 className="chart-title">Response Distribution</h2>
                <ResponseDistributionTable
                  data={jsonData}
                  filterCriteria={filterCriteria}
                />
              </div>
            </div>
          </div>
          {/* Work Context Section */}
          <div
            ref={workContextRef}
            className="D05_Response_Distribution-capture-area"
          >
            <div className="D05_Response_Distribution-row three-columns">
              <div className="D05_Response_Distribution-chart-card">
                <h2 className="chart-title">Work Context - Avg Scores</h2>
                <WorkContextScoresTable filterCriteria={filterCriteria} />
              </div>
              <div className="D05_Response_Distribution-chart-card">
                <h2 className="chart-title">Work Context</h2>
                <BarChartViz02 filterCriteria={filterCriteria} />
              </div>
              <div className="D05_Response_Distribution-chart-card">
                <h2 className="chart-title">Work Context - Distribution</h2>
                <WorkContextDistributionTable
                  data={jsonData}
                  filterCriteria={filterCriteria}
                />
              </div>
            </div>
          </div>

          {/* Job Satisfaction Section */}
          <div
            ref={jobSatisfactionRef}
            className="D05_Response_Distribution-capture-area"
          >
            <div className="D05_Response_Distribution-row three-columns">
              <div className="D05_Response_Distribution-chart-card">
                <h2 className="chart-title">Job Satisfaction - Avg Scores</h2>
                <JobSatisfactionScoresTable filterCriteria={filterCriteria} />
              </div>
              <div className="D05_Response_Distribution-chart-card">
                <h2 className="chart-title">Job Satisfaction</h2>
                <BarChartViz03 filterCriteria={filterCriteria} />
              </div>
              <div className="D05_Response_Distribution-chart-card">
                <h2 className="chart-title">Job Satisfaction - Distribution</h2>
                <JobSatisfactionDistributionTable
                  data={jsonData}
                  filterCriteria={filterCriteria}
                />
              </div>
            </div>
          </div>

          {/* Organizational Commitment Section */}
          <div
            ref={organizationalCommitmentRef}
            className="D05_Response_Distribution-capture-area"
          >
            <div className="D05_Response_Distribution-row three-columns">
              <div className="D05_Response_Distribution-chart-card">
                <h2 className="chart-title">
                  Organizational Commitment - Avg Scores
                </h2>
                <OrganizationalCommitmentScoresTable
                  filterCriteria={filterCriteria}
                />
              </div>
              <div className="D05_Response_Distribution-chart-card">
                <h2 className="chart-title">Organizational Commitment</h2>
                <BarChartViz04 filterCriteria={filterCriteria} />
              </div>
              <div className="D05_Response_Distribution-chart-card">
                <h2 className="chart-title">
                  Organizational Commitment - Distribution
                </h2>
                <OrganizationalCommitmentDistributionTable
                  data={jsonData}
                  filterCriteria={filterCriteria}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Narrative Modal */}
      {isNarrativeOpen && (
        <div className="D05_Response_Distribution-narrative-modal">
          <div className="D05_Response_Distribution-narrative-content">
            <button
              className="D05_Response_Distribution-narrative-close-button"
              onClick={toggleNarrative}
            >
              Close
            </button>
            <h3>Narrative Insights</h3>
            <p style={{ whiteSpace: "pre-wrap" }}>{narrative}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default D05_Response_DistributionDashboard;